import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router'
import { Card } from '@carouselgg/nexus/card'
import { Text } from '@carouselgg/nexus/text'
import { CircleCheck, MapPin } from '@carouselgg/nexus/icon'
import { getMyPasses, type MyPass } from '../api/checkin'
import { LivePass } from '../components/LivePass'
import { venueLine } from './checkInCopy'
import styles from './MyPassesPage.module.css'

/**
 * The code rotates on its own, offline; refetching now and then just keeps the
 * clock correction fresh and picks up check-ins done at the desk.
 */
export const PASS_REFRESH_MS = 5 * 60 * 1000

/**
 * Your check-in passes, for players without the app: open this at the venue
 * and let staff scan the code. Staff see your name and photo when they do —
 * that's the point of checking in at a desk.
 */
export function MyPassesPage() {
  const [passes, setPasses] = useState<MyPass[] | null>(null)
  const [fetchedAt, setFetchedAt] = useState(0)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const res = await getMyPasses()
      setPasses(res.passes)
      setFetchedAt(res.fetchedAt)
      setError('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load your passes')
    }
  }, [])

  useEffect(() => {
    void load()
    const id = setInterval(() => void load(), PASS_REFRESH_MS)
    return () => clearInterval(id)
  }, [load])

  return (
    <div className={styles.page}>
      <Text as="h1" size="2xl" weight="bold">Check-in passes</Text>
      <Text muted>Show your pass at the desk when you arrive. Staff scan it to check you in.</Text>

      {error && <Text className={styles.error}>{error}</Text>}
      {passes === null && !error && <Text muted>Loading…</Text>}
      {passes?.length === 0 && (
        <Text muted>
          No passes right now. They appear here for in-person tournaments you're registered in once check-in opens.
        </Text>
      )}

      <div className={styles.list}>
        {passes?.map((p) => (
          <Card key={p.season.id}>
            <div className={styles.pass}>
              <Text size="sm" muted>{p.season.org.name}</Text>
              <Text as="h2" size="lg" weight="bold">{p.season.name}</Text>
              {venueLine(p.season) && (
                <Text size="sm" muted className={styles.venue}><MapPin size={14} /> {venueLine(p.season)}</Text>
              )}
              <Text size="sm">{p.team ? p.team.name : 'Free agent'}</Text>
              {p.checked_in_at ? (
                <Text className={styles.done}><CircleCheck size={18} /> Checked in</Text>
              ) : (
                <LivePass pass={p.pass} fetchedAt={fetchedAt} size={240} label={`Check-in pass for ${p.season.name}`} />
              )}
            </div>
          </Card>
        ))}
      </div>

      <Text size="sm" muted>
        Have the Carousel app? Your passes are there too. <Link to="/dashboard">Back to dashboard</Link>
      </Text>
    </div>
  )
}
